import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBackRounded, Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import LoginLayout from "../../../layouts/login_layout/login_layout";
import { useFeedback } from "../../../providers/feedback/feedback";
import { getFriendlyServerErrorMessage } from "../../../utils/auth_error_messages";

const RESET_PASSWORD_ENDPOINT =
  "https://resetadminpasswordwithcorekey-f5v5da2yxa-uc.a.run.app";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const [formValues, setFormValues] = useState({
    email: "",
    coreKey: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSensitiveValues, setShowSensitiveValues] = useState(false);

  function validateEmail(email: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  async function handleSubmit() {
    if (
      !formValues.email.trim() ||
      !formValues.coreKey.trim() ||
      !formValues.newPassword ||
      !formValues.confirmPassword
    ) {
      showSnackbar("Please fill all fields");
      return;
    }

    if (!validateEmail(formValues.email)) {
      showSnackbar("Please enter a valid email address");
      return;
    }

    if (formValues.newPassword !== formValues.confirmPassword) {
      showSnackbar("Password and confirm password must match");
      return;
    }

    if (formValues.newPassword.length < 6) {
      showSnackbar("Password should be at least 6 characters");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetch(RESET_PASSWORD_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formValues.email.trim(),
          coreKey: formValues.coreKey.trim(),
          newPassword: formValues.newPassword,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || "Failed to reset password");
      }

      showSnackbar(result?.message || "Password reset successfully");
      navigate("/admin/login");
    } catch (error: any) {
      console.error("Failed to reset admin password", error);
      showSnackbar(
        getFriendlyServerErrorMessage(error, "Failed to reset password"),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <LoginLayout>
      <Grid container justifyContent="center">
        <Grid item lg={8} md={10} sm={12} mb={3} width="100%">
          <Box mt={6}>
            <Button startIcon={<ArrowBackRounded />} onClick={() => navigate("/admin/login")}>
              Back to Login
            </Button>
            <Typography variant="h5" fontWeight={700} mt={2} textAlign="center">
              Reset Admin Password
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1} textAlign="center">
              Enter the authorized reset key and choose a new password.
            </Typography>
          </Box>

          <Box mt={4} display="flex" flexDirection="column" gap={2.5}>
            <TextField
              fullWidth
              type="email"
              label="Admin Email"
              value={formValues.email}
              onChange={(event) =>
                setFormValues((prev) => ({ ...prev, email: event.target.value }))
              }
            />
            <TextField
              fullWidth
              type={showSensitiveValues ? "text" : "password"}
              label="Authorized Reset Key"
              value={formValues.coreKey}
              onChange={(event) =>
                setFormValues((prev) => ({ ...prev, coreKey: event.target.value }))
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowSensitiveValues((prev) => !prev)}>
                      {showSensitiveValues ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              type={showSensitiveValues ? "text" : "password"}
              label="New Password"
              value={formValues.newPassword}
              onChange={(event) =>
                setFormValues((prev) => ({ ...prev, newPassword: event.target.value }))
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowSensitiveValues((prev) => !prev)}>
                      {showSensitiveValues ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              type={showSensitiveValues ? "text" : "password"}
              label="Confirm Password"
              value={formValues.confirmPassword}
              onChange={(event) =>
                setFormValues((prev) => ({
                  ...prev,
                  confirmPassword: event.target.value,
                }))
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowSensitiveValues((prev) => !prev)}>
                      {showSensitiveValues ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Resetting..." : "Reset Password"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </LoginLayout>
  );
}
